import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  AlertTriangle,
  TrendingUp,
  DollarSign,
  Clock,
  FileText,
  HelpCircle,
  PieChart,
  Users,
  MapPin,
  ShoppingBag,
  Check,
  XCircle,
  BookOpen,
  Star,
  Mail,
  Tag,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart as RechartsPie, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { useAdminAuth } from '../context/AdminAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_beauty-bazaar-117/artifacts/2j1k3po7_Reggae%20Essences%20Logo.png";

// Admin Layout Component
export const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAdminAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, navigate]);

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/products', icon: Package, label: 'Products' },
    { path: '/admin/categories', icon: Tag, label: 'Categories' },
    { path: '/admin/orders', icon: ShoppingCart, label: 'Orders' },
    { path: '/admin/reviews', icon: Star, label: 'Reviews' },
    { path: '/admin/blog', icon: BookOpen, label: 'Blog' },
    { path: '/admin/policies', icon: FileText, label: 'Policies' },
    { path: '/admin/about', icon: FileText, label: 'About Page' },
    { path: '/admin/faq', icon: HelpCircle, label: 'FAQ' },
    { path: '/admin/reports', icon: PieChart, label: 'Reports' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-sand flex" data-testid="admin-layout">
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-forest transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="h-full flex flex-col">
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Logo" className="h-10 w-auto rounded-lg" />
              <span className="font-heading text-lg font-semibold text-white">Admin</span>
            </Link>
          </div>

          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${isActive(item.path, item.exact) ? 'bg-white/20 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="p-4 border-t border-white/10">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 text-white/70 hover:text-white transition-colors w-full"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="bg-white border-b px-6 py-4 flex items-center justify-between lg:justify-end">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden p-2 rounded-lg hover:bg-gray-100">
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <span className="text-sm text-gray-500">Reggae Essences Admin</span>
        </header>
        <main className="p-6"><Outlet /></main>
      </div>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
    </div>
  );
};

// Dashboard Page
export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, salesRes, lowStockRes, ordersRes] = await Promise.all([
          axios.get(`${API}/analytics/summary`),
          axios.get(`${API}/analytics/sales-chart`),
          axios.get(`${API}/inventory/low-stock`),
          axios.get(`${API}/orders`),
        ]);
        setStats(statsRes.data);
        setSalesData(salesRes.data);
        setLowStock(lowStockRes.data);
        setRecentOrders(ordersRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return <div className="animate-pulse space-y-6"><div className="grid md:grid-cols-4 gap-6">{[1,2,3,4].map(i => <div key={i} className="h-32 bg-gray-200 rounded-2xl" />)}</div></div>;
  }

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl font-bold text-forest">Dashboard</h1>
        <p className="text-gray-500">Welcome back! Here's what's happening today.</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <TrendingUp className="h-5 w-5 text-green-500" />
          </div>
          <p className="text-sm text-gray-500">Today's Revenue</p>
          <p className="text-2xl font-bold text-forest">${stats?.daily_revenue?.toFixed(2) || '0.00'}</p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-gray-100">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
            <ShoppingCart className="h-6 w-6 text-blue-600" />
          </div>
          <p className="text-sm text-gray-500">Today's Orders</p>
          <p className="text-2xl font-bold text-forest">{stats?.daily_orders || 0}</p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-gray-100">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
            <Eye className="h-6 w-6 text-purple-600" />
          </div>
          <p className="text-sm text-gray-500">Page Views Today</p>
          <p className="text-2xl font-bold text-forest">{stats?.page_views || 0}</p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-gray-100">
          <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
            <Clock className="h-6 w-6 text-orange-600" />
          </div>
          <p className="text-sm text-gray-500">Avg. Session</p>
          <p className="text-2xl font-bold text-forest">{Math.round(stats?.avg_session_duration || 0)}s</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-gray-100">
          <h3 className="font-semibold text-forest mb-6">Sales (Last 7 Days)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={salesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="date" stroke="#666" fontSize={12} />
                <YAxis stroke="#666" fontSize={12} />
                <Tooltip />
                <Line type="monotone" dataKey="revenue" stroke="#2D4A3E" strokeWidth={2} dot={{ fill: '#2D4A3E' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-gray-100">
          <div className="flex items-center gap-2 mb-6">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            <h3 className="font-semibold text-forest">Low Stock Alert</h3>
          </div>
          {lowStock.length === 0 ? (
            <p className="text-gray-500 text-center py-8">All products are well stocked!</p>
          ) : (
            <div className="space-y-3">
              {lowStock.slice(0, 5).map((product) => (
                <div key={product.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <img src={product.images?.[0] || 'https://images.unsplash.com/photo-1672736810868-130c00ef5a9e?w=600&q=80'} alt={product.name} className="w-10 h-10 rounded-lg object-cover" onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1672736810868-130c00ef5a9e?w=600&q=80'; }} />
                    <span className="font-medium">{product.name}</span>
                  </div>
                  <span className={`font-mono text-sm ${product.stock === 0 ? 'text-red-600' : 'text-orange-600'}`}>
                    {product.stock} left
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white p-6 rounded-2xl border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-semibold text-forest">Recent Orders</h3>
          <Link to="/admin/orders" className="text-sm text-gold hover:underline">View All</Link>
        </div>
        {recentOrders.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No orders yet</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentOrders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell className="font-medium">{order.customer_name}</TableCell>
                  <TableCell>{order.items?.length || 0} items</TableCell>
                  <TableCell>${order.total_amount?.toFixed(2)}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${order.status === 'confirmed' ? 'bg-green-100 text-green-700' : order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`}>
                      {order.status}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

// Products Management
export const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(true);

  const fetchProducts = async () => {
    try {
      // Include inactive products in admin view
      const response = await axios.get(`${API}/products?include_inactive=true`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const toggleProductActive = async (id) => {
    try {
      const response = await axios.put(`${API}/products/${id}/toggle-active`);
      toast.success(response.data.message);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update product status');
    }
  };

  const filteredProducts = showInactive ? products : products.filter(p => p.active !== false);

  return (
    <div data-testid="admin-products" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-forest">Products</h1>
          <p className="text-gray-500">Manage your product catalog</p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show inactive products
          </label>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setEditingProduct(null)} className="bg-forest text-white rounded-full" data-testid="add-product-btn">
                <Plus className="h-5 w-5 mr-2" /> Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
              </DialogHeader>
              <ProductForm product={editingProduct} onSuccess={() => { setIsDialogOpen(false); fetchProducts(); }} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-gray-200 rounded-xl" />)}</div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProducts.map((product) => (
                <TableRow key={product.id} className={product.active === false ? 'opacity-60 bg-gray-50' : ''}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <img src={product.images?.[0] || 'https://images.unsplash.com/photo-1672736810868-130c00ef5a9e?w=600&q=80'} alt={product.name} className="w-12 h-12 rounded-lg object-cover" onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1672736810868-130c00ef5a9e?w=600&q=80'; }} />
                      <span className="font-medium">{product.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="capitalize">{product.category?.replace(/_/g, ' ')}</TableCell>
                  <TableCell>${product.price?.toFixed(2)}</TableCell>
                  <TableCell>
                    <span className={`font-mono ${product.stock === 0 ? 'text-red-600' : product.stock <= 5 ? 'text-orange-600' : 'text-green-600'}`}>
                      {product.stock}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${product.active === false ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'}`}>
                      {product.active === false ? 'Inactive' : 'Active'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => toggleProductActive(product.id)}
                        title={product.active === false ? 'Activate product' : 'Deactivate product'}
                      >
                        {product.active === false ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setEditingProduct(product); setIsDialogOpen(true); }}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(product.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

// Product Form Component
const ProductForm = ({ product, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    price: product?.price || '',
    category: product?.category || '',
    stock: product?.stock || 0,
    images: product?.images || [],
    videos: product?.videos || [],
    ingredients: product?.ingredients || '',
    usage_instructions: product?.usage_instructions || '',
  });
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [productCategories, setProductCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/categories`).then(res => setProductCategories(res.data)).catch(() => {});
  }, []);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    try {
      const uploadedUrls = [];
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        const response = await axios.post(`${API}/upload/image`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        // Construct full URL for the uploaded image
        const baseUrl = process.env.REACT_APP_BACKEND_URL;
        uploadedUrls.push(`${baseUrl}${response.data.url}`);
      }
      setFormData({ ...formData, images: [...formData.images, ...uploadedUrls] });
      toast.success(`${uploadedUrls.length} image(s) uploaded`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingVideo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await axios.post(`${API}/upload/video`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      setFormData({ ...formData, videos: [...formData.videos, `${baseUrl}${response.data.url}`] });
      toast.success('Video uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload video');
    } finally {
      setUploadingVideo(false);
    }
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  const removeVideo = (index) => {
    const newVideos = formData.videos.filter((_, i) => i !== index);
    setFormData({ ...formData, videos: newVideos });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
      };

      if (product) {
        await axios.put(`${API}/products/${product.id}`, data);
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/products`, data);
        toast.success('Product created');
      }
      onSuccess();
    } catch (error) {
      toast.error('Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Product Name *</label>
          <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Category *</label>
          <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
            <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>
              {productCategories.map(cat => (
                <SelectItem key={cat.slug} value={cat.slug}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Description *</label>
        <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required rows={3} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Price (USD) *</label>
          <Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Stock Quantity *</label>
          <Input type="number" value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} required />
        </div>
      </div>

      {/* Image Upload Section */}
      <div>
        <label className="block text-sm font-medium mb-2">Product Images</label>
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-4">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
            className="hidden"
            id="image-upload"
            disabled={uploadingImages}
          />
          <label htmlFor="image-upload" className="cursor-pointer block text-center py-4 text-gray-500 hover:text-forest">
            {uploadingImages ? 'Uploading...' : 'Click to upload images or drag and drop'}
          </label>
          
          {formData.images.length > 0 && (
            <div className="grid grid-cols-4 gap-3 mt-4">
              {formData.images.map((img, index) => (
                <div key={index} className="relative group">
                  <img src={img} alt="" className="w-full h-20 object-cover rounded-lg" />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Video Upload Section */}
      <div>
        <label className="block text-sm font-medium mb-2">Product Videos</label>
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-4">
          <input
            type="file"
            accept="video/*"
            onChange={handleVideoUpload}
            className="hidden"
            id="video-upload"
            disabled={uploadingVideo}
          />
          <label htmlFor="video-upload" className="cursor-pointer block text-center py-4 text-gray-500 hover:text-forest">
            {uploadingVideo ? 'Uploading...' : 'Click to upload a video'}
          </label>
          
          {formData.videos.length > 0 && (
            <div className="space-y-2 mt-4">
              {formData.videos.map((vid, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <span className="text-sm truncate flex-1">{vid}</span>
                  <button
                    type="button"
                    onClick={() => removeVideo(index)}
                    className="ml-2 text-red-500 hover:bg-red-50 p-1 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">Or paste YouTube/Vimeo embed URLs below:</p>
        <Input 
          placeholder="https://youtube.com/embed/..." 
          className="mt-2"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const url = e.target.value.trim();
              if (url) {
                setFormData({ ...formData, videos: [...formData.videos, url] });
                e.target.value = '';
              }
            }
          }}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Ingredients</label>
        <Textarea value={formData.ingredients} onChange={(e) => setFormData({ ...formData, ingredients: e.target.value })} rows={2} />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Usage Instructions</label>
        <Textarea value={formData.usage_instructions} onChange={(e) => setFormData({ ...formData, usage_instructions: e.target.value })} rows={2} />
      </div>

      <Button type="submit" disabled={loading} className="w-full bg-forest text-white">
        {loading ? 'Saving...' : product ? 'Update Product' : 'Create Product'}
      </Button>
    </form>
  );
};

// Orders Management
export const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingCarrier, setTrackingCarrier] = useState('USPS');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [resendingInvoice, setResendingInvoice] = useState(false);
  const [updatingTracking, setUpdatingTracking] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelMessage, setCancelMessage] = useState('');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const cancellationReasons = [
    'Item out of stock',
    'Pricing error on the website',
    'Unable to verify payment',
    'Shipping restrictions to the delivery location',
    'Address invalid or undeliverable',
    'Discontinued product',
    'System or technical errors',
  ];

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const sendMessage = async () => {
    if (!selectedOrder || !message.trim()) return;
    setSendingMessage(true);
    try {
      await axios.post(`${API}/orders/${selectedOrder.id}/message`, { message: message.trim() });
      toast.success('Message sent to customer');
      setMessageDialogOpen(false);
      setMessage('');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const shipOrder = async () => {
    if (!selectedOrder || !trackingNumber.trim()) return;
    setUpdatingTracking(true);
    try {
      await axios.put(`${API}/orders/${selectedOrder.id}/tracking`, {
        tracking_number: trackingNumber.trim(),
        tracking_carrier: trackingCarrier
      });
      toast.success('Order shipped! Customer notified with tracking details.');
      setTrackingDialogOpen(false);
      setTrackingNumber('');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to ship order');
    } finally {
      setUpdatingTracking(false);
    }
  };

  const markDelivered = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/deliver`);
      toast.success('Order marked as delivered');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const closeOrder = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/close`);
      toast.success('Order closed');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to close order');
    }
  };

  const cancelOrder = async () => {
    if (!selectedOrder || !cancelReason) return;
    setCancellingOrder(true);
    try {
      await axios.put(`${API}/orders/${selectedOrder.id}/cancel`, {
        reason: cancelReason,
        message: cancelMessage.trim() || null
      });
      toast.success('Order cancelled and customer notified');
      setCancelDialogOpen(false);
      setOrderDetailsOpen(false);
      setCancelReason('');
      setCancelMessage('');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setCancellingOrder(false);
    }
  };

  const markAsPaid = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/mark-paid`);
      toast.success('Order marked as paid');
      fetchOrders();
      // Update selected order if viewing details
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, payment_status: 'paid', status: selectedOrder.status === 'pending' ? 'confirmed' : selectedOrder.status });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark order as paid');
    }
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const toggleAllOrders = () => {
    if (selectedOrders.length === filteredOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map(o => o.id));
    }
  };

  const deleteSelectedOrders = async () => {
    if (deleteConfirmText.toLowerCase() !== 'delete') {
      toast.error('Please type "delete" to confirm');
      return;
    }
    
    setDeleting(true);
    try {
      if (selectedOrders.length === 1) {
        await axios.delete(`${API}/orders/${selectedOrders[0]}`);
      } else {
        await axios.post(`${API}/orders/bulk-delete`, { order_ids: selectedOrders });
      }
      toast.success(`Successfully deleted ${selectedOrders.length} order(s)`);
      setDeleteDialogOpen(false);
      setDeleteConfirmText('');
      setSelectedOrders([]);
      setOrderDetailsOpen(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete orders');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (orderId = null) => {
    if (orderId) {
      setSelectedOrders([orderId]);
    }
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setOrderDetailsOpen(true);
  };

  const filteredOrders = filterStatus === 'all' 
    ? orders 
    : orders.filter(o => o.status === filterStatus);

  const carriers = ['USPS', 'UPS', 'FedEx', 'DHL', 'Other'];

  const getStatusBadge = (status) => {
    const styles = {
      delivered: 'bg-green-100 text-green-700',
      shipped: 'bg-blue-100 text-blue-700',
      processing: 'bg-purple-100 text-purple-700',
      confirmed: 'bg-yellow-100 text-yellow-700',
      cancelled: 'bg-red-100 text-red-700',
      pending: 'bg-gray-100 text-gray-700',
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const getPaymentBadge = (status) => {
    return status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700';
  };

  // Generate short order number from ID
  const getOrderNumber = (id) => {
    return `#${id.slice(0, 8).toUpperCase()}`;
  };

  // Get product names as a summary
  const getProductSummary = (items) => {
    if (!items || items.length === 0) return 'No items';
    if (items.length === 1) return items[0].product_name;
    return `${items[0].product_name} +${items.length - 1} more`;
  };

  return (
    <div data-testid="admin-orders" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-forest">Orders</h1>
          <p className="text-gray-500">Manage customer orders</p>
        </div>
        <div className="flex items-center gap-4">
          {selectedOrders.length > 0 && (
            <Button 
              variant="outline" 
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => openDeleteDialog()}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete ({selectedOrders.length})
            </Button>
          )}
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Orders</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-12 bg-gray-200 rounded-xl" />)}</div>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl text-center">
          <ShoppingCart className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No orders found</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-sand/50">
                <TableHead className="w-12">
                  <input
                    type="checkbox"
                    checked={selectedOrders.length === filteredOrders.length && filteredOrders.length > 0}
                    onChange={toggleAllOrders}
                    className="rounded border-gray-300"
                  />
                </TableHead>
                <TableHead className="font-semibold">Order #</TableHead>
                <TableHead className="font-semibold">Date</TableHead>
                <TableHead className="font-semibold">Customer</TableHead>
                <TableHead className="font-semibold">Products</TableHead>
                <TableHead className="font-semibold">Total</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Payment</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.map((order) => (
                <TableRow 
                  key={order.id} 
                  className={`cursor-pointer hover:bg-sand/30 transition-colors ${order.closed ? 'opacity-60' : ''} ${selectedOrders.includes(order.id) ? 'bg-blue-50' : ''}`}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedOrders.includes(order.id)}
                      onChange={() => toggleOrderSelection(order.id)}
                      className="rounded border-gray-300"
                    />
                  </TableCell>
                  <TableCell onClick={() => openOrderDetails(order)}>
                    <span className="font-mono text-sm text-forest font-semibold hover:underline">
                      {getOrderNumber(order.id)}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {new Date(order.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-gray-900">{order.customer_name}</p>
                      <p className="text-xs text-gray-500">{order.customer_email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm text-gray-700 max-w-[200px] truncate" title={order.items?.map(i => i.product_name).join(', ')}>
                      {getProductSummary(order.items)}
                    </p>
                  </TableCell>
                  <TableCell className="font-semibold text-forest">
                    ${order.total_amount?.toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(order.status)}`}>
                      {order.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getPaymentBadge(order.payment_status)}`}>
                      {order.payment_status}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Order Details Modal */}
      <Dialog open={orderDetailsOpen} onOpenChange={setOrderDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedOrder && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>Order {getOrderNumber(selectedOrder.id)}</span>
                  <div className="flex gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(selectedOrder.status)}`}>
                      {selectedOrder.status}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getPaymentBadge(selectedOrder.payment_status)}`}>
                      {selectedOrder.payment_status}
                    </span>
                  </div>
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* Customer Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-sand/50 rounded-xl">
                    <h4 className="font-semibold text-forest mb-2 flex items-center gap-2">
                      <Users className="h-4 w-4" /> Customer Details
                    </h4>
                    <p className="font-medium">{selectedOrder.customer_name}</p>
                    <p className="text-sm text-gray-600">{selectedOrder.customer_email}</p>
                    <p className="text-sm text-gray-600">{selectedOrder.customer_phone}</p>
                  </div>
                  <div className="p-4 bg-sand/50 rounded-xl">
                    <h4 className="font-semibold text-forest mb-2 flex items-center gap-2">
                      <MapPin className="h-4 w-4" /> Delivery Details
                    </h4>
                    <p className="text-sm text-gray-700">{selectedOrder.delivery_address}</p>
                    <p className="text-sm text-gray-600">Date: {selectedOrder.delivery_date}</p>
                    {selectedOrder.notes && <p className="text-sm text-gray-500 mt-1">Notes: {selectedOrder.notes}</p>}
                  </div>
                </div>

                {/* Tracking Info */}
                {selectedOrder.tracking_number && (
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                      <Package className="h-4 w-4" /> Tracking Information
                    </h4>
                    <p className="text-sm text-blue-700">
                      {selectedOrder.tracking_carrier}: <span className="font-mono">{selectedOrder.tracking_number}</span>
                    </p>
                    {selectedOrder.shipped_at && (
                      <p className="text-xs text-blue-600 mt-1">
                        Shipped: {new Date(selectedOrder.shipped_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}

                {/* Order Items */}
                <div>
                  <h4 className="font-semibold text-forest mb-3 flex items-center gap-2">
                    <ShoppingBag className="h-4 w-4" /> Order Items
                  </h4>
                  <div className="border rounded-xl overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Product</TableHead>
                          <TableHead className="text-center">Qty</TableHead>
                          <TableHead className="text-right">Price</TableHead>
                          <TableHead className="text-right">Subtotal</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedOrder.items?.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{item.product_name}</TableCell>
                            <TableCell className="text-center">{item.quantity}</TableCell>
                            <TableCell className="text-right">${item.price?.toFixed(2)}</TableCell>
                            <TableCell className="text-right font-medium">${item.subtotal?.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-gray-50">
                          <TableCell colSpan={3} className="text-right text-sm text-gray-600">Subtotal</TableCell>
                          <TableCell className="text-right text-sm">${(selectedOrder.items?.reduce((sum, item) => sum + (item.subtotal || item.price * item.quantity), 0))?.toFixed(2)}</TableCell>
                        </TableRow>
                        {(selectedOrder.shipping_cost != null && selectedOrder.shipping_cost > 0) && (
                          <TableRow className="bg-gray-50">
                            <TableCell colSpan={3} className="text-right text-sm text-gray-600">Shipping</TableCell>
                            <TableCell className="text-right text-sm">${selectedOrder.shipping_cost?.toFixed(2)}</TableCell>
                          </TableRow>
                        )}
                        {(selectedOrder.tax_amount != null && selectedOrder.tax_amount > 0) && (
                          <TableRow className="bg-gray-50">
                            <TableCell colSpan={3} className="text-right text-sm text-gray-600">Tax</TableCell>
                            <TableCell className="text-right text-sm">${selectedOrder.tax_amount?.toFixed(2)}</TableCell>
                          </TableRow>
                        )}
                        <TableRow className="bg-gray-50">
                          <TableCell colSpan={3} className="text-right font-semibold">Total</TableCell>
                          <TableCell className="text-right font-bold text-forest">${selectedOrder.total_amount?.toFixed(2)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Communication History */}
                {selectedOrder.messages && selectedOrder.messages.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-forest mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4" /> Communication History
                    </h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto border rounded-xl p-3">
                      {selectedOrder.messages.map((msg, idx) => (
                        <div key={idx} className="text-sm p-3 bg-gray-50 rounded-lg">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span className="capitalize font-medium">{msg.sender}</span>
                            <span>{new Date(msg.created_at).toLocaleString()}</span>
                          </div>
                          <p className="text-gray-700">{msg.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                {!selectedOrder.closed && (
                  <div className="flex flex-wrap gap-3 pt-4 border-t">
                    {/* Mark as Paid Button */}
                    {selectedOrder.payment_status !== 'paid' && selectedOrder.status !== 'cancelled' && (
                      <Button 
                        variant="outline" 
                        className="rounded-full text-green-600 border-green-200 hover:bg-green-50"
                        onClick={() => markAsPaid(selectedOrder.id)}
                      >
                        <DollarSign className="h-4 w-4 mr-2" /> Mark as Paid
                      </Button>
                    )}

                    {/* Message Button */}
                    <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="rounded-full">
                          <FileText className="h-4 w-4 mr-2" /> Message Customer
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Send Message to Customer</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <p className="text-sm text-gray-500">Sending to: {selectedOrder.customer_email}</p>
                          <Textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Type your message..."
                            rows={4}
                          />
                          <Button onClick={sendMessage} disabled={sendingMessage || !message.trim()} className="w-full bg-forest text-white">
                            {sendingMessage ? 'Sending...' : 'Send Message'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>

                    {/* Resend Invoice Button */}
                    <Button
                      variant="outline"
                      className="rounded-full"
                      data-testid="resend-invoice-btn"
                      disabled={resendingInvoice}
                      onClick={async () => {
                        setResendingInvoice(true);
                        try {
                          await axios.post(`${API}/admin/orders/${selectedOrder.id}/resend-invoice`);
                          toast.success('Invoice sent to ' + selectedOrder.customer_email);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to send invoice');
                        } finally {
                          setResendingInvoice(false);
                        }
                      }}
                    >
                      <Mail className="h-4 w-4 mr-2" /> {resendingInvoice ? 'Sending...' : 'Resend Invoice'}
                    </Button>

                    {/* Cancel Order Button */}
                    {selectedOrder.status !== 'cancelled' && selectedOrder.status !== 'delivered' && (
                      <Dialog open={cancelDialogOpen} onOpenChange={(open) => { setCancelDialogOpen(open); if (!open) { setCancelReason(''); setCancelMessage(''); } }}>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
                            <XCircle className="h-4 w-4 mr-2" /> Cancel Order
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle className="text-red-600">Cancel Order</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                              <p className="text-sm text-red-700">
                                <strong>Warning:</strong> This will cancel the order for <strong>{selectedOrder.customer_name}</strong> and send them a notification email.
                              </p>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium mb-2">Cancellation Reason *</label>
                              <Select value={cancelReason} onValueChange={setCancelReason}>
                                <SelectTrigger data-testid="cancel-reason-select">
                                  <SelectValue placeholder="Select a reason..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {cancellationReasons.map(reason => (
                                    <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium mb-2">Additional Message (Optional)</label>
                              <Textarea
                                value={cancelMessage}
                                onChange={(e) => setCancelMessage(e.target.value)}
                                placeholder="Add a personal message to the customer..."
                                rows={3}
                                data-testid="cancel-message-input"
                              />
                            </div>
                            
                            <p className="text-sm text-gray-500">
                              The customer will receive an email with the cancellation reason and your message.
                            </p>
                            
                            <div className="flex gap-3">
                              <Button variant="outline" className="flex-1" onClick={() => setCancelDialogOpen(false)}>
                                Keep Order
                              </Button>
                              <Button 
                                onClick={cancelOrder} 
                                disabled={cancellingOrder || !cancelReason} 
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                                data-testid="confirm-cancel-btn"
                              >
                                {cancellingOrder ? 'Cancelling...' : 'Confirm Cancellation'}
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}

                    {/* Ship Order Button */}
                    {selectedOrder.status !== 'shipped' && selectedOrder.status !== 'delivered' && selectedOrder.status !== 'cancelled' && (
                      <Dialog open={trackingDialogOpen} onOpenChange={setTrackingDialogOpen}>
                        <DialogTrigger asChild>
                          <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full">
                            <Package className="h-4 w-4 mr-2" /> Ship Order
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Ship Order</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium mb-2">Tracking Number *</label>
                              <Input
                                value={trackingNumber}
                                onChange={(e) => setTrackingNumber(e.target.value)}
                                placeholder="Enter tracking number"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Carrier</label>
                              <Select value={trackingCarrier} onValueChange={setTrackingCarrier}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {carriers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <p className="text-sm text-gray-500">Customer will be notified via email with tracking details.</p>
                            <Button onClick={shipOrder} disabled={updatingTracking || !trackingNumber.trim()} className="w-full bg-blue-600 text-white">
                              {updatingTracking ? 'Shipping...' : 'Ship & Notify Customer'}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}

                    {/* Mark Delivered Button */}
                    {selectedOrder.status === 'shipped' && (
                      <Button onClick={() => { markDelivered(selectedOrder.id); setOrderDetailsOpen(false); }} className="bg-green-600 hover:bg-green-700 text-white rounded-full">
                        <Check className="h-4 w-4 mr-2" /> Mark Delivered
                      </Button>
                    )}

                    {/* Close Order Button */}
                    {selectedOrder.status === 'delivered' && !selectedOrder.closed && (
                      <Button variant="outline" onClick={() => { closeOrder(selectedOrder.id); setOrderDetailsOpen(false); }} className="rounded-full">
                        Close Order
                      </Button>
                    )}
                  </div>
                )}

                {/* Order metadata */}
                <div className="text-xs text-gray-400 pt-2 border-t">
                  <p>Order ID: {selectedOrder.id}</p>
                  <p>Created: {new Date(selectedOrder.created_at).toLocaleString()}</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Order(s)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-700">
                <strong>Warning:</strong> You are about to permanently delete <strong>{selectedOrders.length}</strong> order(s). This action cannot be undone.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">
                Type <span className="font-bold text-red-600">delete</span> to confirm:
              </label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type 'delete' to confirm"
                className="font-mono"
                data-testid="delete-confirm-input"
              />
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => { setDeleteDialogOpen(false); setDeleteConfirmText(''); }}
              >
                Cancel
              </Button>
              <Button 
                onClick={deleteSelectedOrders} 
                disabled={deleting || deleteConfirmText.toLowerCase() !== 'delete'} 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn"
              >
                {deleting ? 'Deleting...' : `Delete ${selectedOrders.length} Order(s)`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Analytics Page
export const AdminAnalytics = () => {
  const [stats, setStats] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, salesRes] = await Promise.all([
          axios.get(`${API}/analytics/summary`),
          axios.get(`${API}/analytics/sales-chart`),
        ]);
        setStats(statsRes.data);
        setSalesData(salesRes.data);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="animate-pulse"><div className="h-80 bg-gray-200 rounded-2xl" /></div>;

  return (
    <div data-testid="admin-analytics" className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-forest">Analytics</h1>
        <p className="text-gray-500">Track your store performance</p>
      </div>

      <div className="bg-white p-6 rounded-2xl border border-gray-100">
        <h3 className="font-semibold text-forest mb-6">Revenue (Last 7 Days)</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={salesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="date" stroke="#666" fontSize={12} />
              <YAxis stroke="#666" fontSize={12} />
              <Tooltip />
              <Bar dataKey="revenue" fill="#2D4A3E" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-gray-100">
          <h3 className="font-semibold text-forest mb-6">Top Pages</h3>
          {stats?.top_pages?.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No data yet</p>
          ) : (
            <div className="space-y-3">
              {stats?.top_pages?.map((page, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-gray-600 truncate">{page.page || 'Home'}</span>
                  <span className="font-mono text-sm">{page.views} views</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white p-6 rounded-2xl border border-gray-100">
          <h3 className="font-semibold text-forest mb-6">Visitor Locations</h3>
          {stats?.locations?.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No data yet</p>
          ) : (
            <div className="space-y-3">
              {stats?.locations?.map((loc, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-gray-600">{loc.location || 'Unknown'}</span>
                  <span className="font-mono text-sm">{loc.visits} visits</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Settings Page
const MigrateImagesButton = () => {
  const [migrating, setMigrating] = useState(false);
  const [result, setResult] = useState(null);

  const handleMigrate = async () => {
    setMigrating(true);
    setResult(null);
    try {
      const response = await axios.post(`${API}/admin/migrate-images`);
      setResult(response.data);
      toast.success(`Migration complete! ${response.data.migrated} products updated.`);
    } catch (error) {
      toast.error('Migration failed. Please try again.');
    } finally {
      setMigrating(false);
    }
  };

  return (
    <div>
      <Button
        onClick={handleMigrate}
        disabled={migrating}
        data-testid="migrate-images-btn"
        className="bg-forest text-white rounded-full"
      >
        {migrating ? 'Migrating...' : 'Migrate Images to Cloud Storage'}
      </Button>
      {result && (
        <div className="mt-3 p-3 bg-green-50 rounded-xl text-sm">
          <p className="text-green-700 font-medium">{result.message}</p>
          {result.errors?.length > 0 && (
            <div className="mt-2 text-amber-600">
              <p className="font-medium">Warnings:</p>
              {result.errors.map((err, i) => <p key={i}>{err}</p>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const { logout } = useAdminAuth();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addEmail = () => {
    if (emailInput && !settings.admin_emails.includes(emailInput)) {
      setSettings({ ...settings, admin_emails: [...settings.admin_emails, emailInput] });
      setEmailInput('');
    }
  };

  const removeEmail = (email) => {
    setSettings({ ...settings, admin_emails: settings.admin_emails.filter(e => e !== email) });
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    setChangingPassword(true);
    
    // Validate current password
    const CURRENT_PASSWORD = 'Nadinessence1!';
    if (passwordForm.currentPassword !== CURRENT_PASSWORD) {
      toast.error('Current password is incorrect');
      setChangingPassword(false);
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      setChangingPassword(false);
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      setChangingPassword(false);
      return;
    }
    
    // Note: In production, this would be handled by the backend
    // For now, we show a success message and logout to re-authenticate
    setTimeout(() => {
      toast.success('Password changed successfully! Please log in with your new password.');
      setChangingPassword(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      // Note: The new password would need to be updated in AdminAuthContext.js
      // For this demo, we just logout
      logout();
    }, 1000);
  };

  if (loading) return <div className="animate-pulse"><div className="h-64 bg-gray-200 rounded-2xl" /></div>;

  return (
    <div data-testid="admin-settings" className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-forest">Settings</h1>
        <p className="text-gray-500">Configure your store settings</p>
      </div>

      <div className="bg-white p-6 rounded-2xl border border-gray-100 space-y-6">
        <div>
          <h3 className="font-semibold text-forest mb-4">Business Information</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Business Name</label>
              <Input value={settings?.business_name || ''} onChange={(e) => setSettings({ ...settings, business_name: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Business Email</label>
              <Input type="email" value={settings?.business_email || ''} onChange={(e) => setSettings({ ...settings, business_email: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Business Phone</label>
              <Input value={settings?.business_phone || ''} onChange={(e) => setSettings({ ...settings, business_phone: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">WhatsApp Number</label>
              <Input value={settings?.whatsapp_number || ''} onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })} placeholder="+1234567890" />
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-semibold text-forest mb-4">Notification Emails</h3>
          <div className="flex gap-2 mb-4">
            <Input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder="Enter email address" />
            <Button onClick={addEmail} variant="outline">Add</Button>
          </div>
          <div className="space-y-2">
            {settings?.admin_emails?.map((email, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-sand rounded-xl">
                <span>{email}</span>
                <Button variant="ghost" size="sm" onClick={() => removeEmail(email)} className="text-red-500 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-semibold text-forest mb-4">Social Media</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Facebook URL</label>
              <Input value={settings?.facebook_url || ''} onChange={(e) => setSettings({ ...settings, facebook_url: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Instagram URL</label>
              <Input value={settings?.instagram_url || ''} onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })} />
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-semibold text-forest mb-4">Inventory</h3>
          <div className="max-w-xs">
            <label className="block text-sm font-medium mb-2">Low Stock Threshold</label>
            <Input type="number" value={settings?.low_stock_threshold || 5} onChange={(e) => setSettings({ ...settings, low_stock_threshold: parseInt(e.target.value) })} />
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving} className="bg-forest text-white rounded-full">
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Image Migration Section */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100">
        <h3 className="font-semibold text-forest mb-2">Image Storage Migration</h3>
        <p className="text-sm text-gray-500 mb-4">
          Migrate all product images to persistent cloud storage. This ensures images survive redeployments. Run this once after deploying.
        </p>
        <MigrateImagesButton />
      </div>

      {/* Change Password Section */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100">
        <h3 className="font-semibold text-forest mb-4">Change Admin Password</h3>
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium mb-2">Current Password</label>
            <Input 
              type="password" 
              value={passwordForm.currentPassword} 
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} 
              required
              data-testid="current-password-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">New Password</label>
            <Input 
              type="password" 
              value={passwordForm.newPassword} 
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} 
              required
              minLength={8}
              data-testid="new-password-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Confirm New Password</label>
            <Input 
              type="password" 
              value={passwordForm.confirmPassword} 
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} 
              required
              data-testid="confirm-password-input"
            />
          </div>
          <Button type="submit" disabled={changingPassword} variant="outline" className="border-terracotta text-terracotta hover:bg-terracotta hover:text-white">
            {changingPassword ? 'Changing...' : 'Change Password'}
          </Button>
        </form>
        <p className="text-sm text-gray-500 mt-4">
          Note: After changing password, you'll need to update the password in the system configuration.
        </p>
      </div>
    </div>
  );
};

// About Page Management
export const AdminAbout = () => {
  const [content, setContent] = useState({
    hero_title: 'About Reggae Essences',
    hero_subtitle: 'Naturally rooted. Soulfully made.',
    main_story: '',
    promise_text: '',
    brand_personality: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content/about`);
        setContent(response.data);
      } catch (error) {
        console.error('Error fetching about content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/about`, content);
      toast.success('About page updated');
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="animate-pulse"><div className="h-64 bg-gray-200 rounded-2xl" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-forest">About Page</h1>
        <p className="text-gray-500">Edit your About page content</p>
      </div>

      <div className="bg-white p-6 rounded-2xl border border-gray-100 space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">Hero Title</label>
          <Input value={content.hero_title} onChange={(e) => setContent({ ...content, hero_title: e.target.value })} />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Hero Subtitle</label>
          <Input value={content.hero_subtitle} onChange={(e) => setContent({ ...content, hero_subtitle: e.target.value })} />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Main Story</label>
          <Textarea value={content.main_story} onChange={(e) => setContent({ ...content, main_story: e.target.value })} rows={6} placeholder="Tell your brand story..." />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Our Promise</label>
          <Textarea value={content.promise_text} onChange={(e) => setContent({ ...content, promise_text: e.target.value })} rows={4} placeholder="What you promise to customers..." />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Brand Personality</label>
          <Textarea value={content.brand_personality} onChange={(e) => setContent({ ...content, brand_personality: e.target.value })} rows={2} placeholder="Your brand's vibe in one sentence..." />
        </div>

        <Button onClick={handleSave} disabled={saving} className="bg-forest text-white rounded-full">
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

// Category Management
export const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    try {
      await axios.post(`${API}/categories`, { name: newName.trim() });
      setNewName('');
      toast.success('Category added');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add category');
    }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try {
      const slug = editName.trim().toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and');
      await axios.put(`${API}/categories/${id}`, { name: editName.trim(), slug });
      setEditingId(null);
      toast.success('Category updated');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category? Products in this category will keep their current category value.')) return;
    try {
      await axios.delete(`${API}/categories/${id}`);
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  return (
    <div data-testid="admin-categories" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold text-forest">Product Categories</h2>
      </div>

      {/* Add new category */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100">
        <h3 className="font-semibold text-forest mb-4">Add New Category</h3>
        <div className="flex gap-3">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Bath Bombs, Essential Oils..."
            className="flex-1"
            data-testid="new-category-input"
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          />
          <Button onClick={handleAdd} className="bg-forest text-white rounded-full" data-testid="add-category-btn">
            <Plus className="h-4 w-4 mr-2" /> Add
          </Button>
        </div>
      </div>

      {/* Category list */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : categories.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No categories yet</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((cat) => (
                <TableRow key={cat.id}>
                  <TableCell>
                    {editingId === cat.id ? (
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleUpdate(cat.id)}
                        className="max-w-xs"
                        autoFocus
                      />
                    ) : (
                      <span className="font-medium">{cat.name}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-gray-500 font-mono text-sm">{cat.slug}</TableCell>
                  <TableCell className="text-right">
                    {editingId === cat.id ? (
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" onClick={() => handleUpdate(cat.id)} className="bg-forest text-white">Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => { setEditingId(cat.id); setEditName(cat.name); }} data-testid={`edit-category-${cat.slug}`}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDelete(cat.id)} className="text-red-600 hover:bg-red-50" data-testid={`delete-category-${cat.slug}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

// FAQ Management
export const AdminFAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFaq, setEditingFaq] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API}/content/faq`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFaqs(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await axios.delete(`${API}/content/faq/${id}`);
      toast.success('FAQ deleted');
      fetchFaqs();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-forest">FAQ Management</h1>
          <p className="text-gray-500">Manage frequently asked questions</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditingFaq(null)} className="bg-forest text-white rounded-full">
              <Plus className="h-5 w-5 mr-2" /> Add FAQ
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingFaq ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle>
            </DialogHeader>
            <FAQForm faq={editingFaq} onSuccess={() => { setIsDialogOpen(false); fetchFaqs(); }} />
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-gray-200 rounded-xl" />)}</div>
      ) : faqs.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl text-center">
          <HelpCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No FAQs yet. Add your first one!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {faqs.map((faq) => (
            <div key={faq.id} className="bg-white p-6 rounded-2xl border border-gray-100">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <span className="text-xs font-mono text-gold uppercase">{faq.category}</span>
                  <h3 className="font-semibold text-forest mt-1">{faq.question}</h3>
                  <p className="text-gray-600 mt-2 text-sm">{faq.answer}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setEditingFaq(faq); setIsDialogOpen(true); }}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(faq.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FAQForm = ({ faq, onSuccess }) => {
  const [formData, setFormData] = useState({
    category: faq?.category || 'Products',
    question: faq?.question || '',
    answer: faq?.answer || '',
    order: faq?.order || 0,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (faq) {
        await axios.put(`${API}/content/faq/${faq.id}`, formData);
        toast.success('FAQ updated');
      } else {
        await axios.post(`${API}/content/faq`, formData);
        toast.success('FAQ created');
      }
      onSuccess();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">Category</label>
        <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Products">Products</SelectItem>
            <SelectItem value="Orders & Shipping">Orders & Shipping</SelectItem>
            <SelectItem value="Returns & Refunds">Returns & Refunds</SelectItem>
            <SelectItem value="Account & Membership">Account & Membership</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">Question</label>
        <Input value={formData.question} onChange={(e) => setFormData({ ...formData, question: e.target.value })} required />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">Answer</label>
        <Textarea value={formData.answer} onChange={(e) => setFormData({ ...formData, answer: e.target.value })} rows={4} required />
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-forest text-white">
        {loading ? 'Saving...' : faq ? 'Update FAQ' : 'Add FAQ'}
      </Button>
    </form>
  );
};

// Reports Page
export const AdminReports = () => {
  const [activeTab, setActiveTab] = useState('inventory');
  const [inventoryData, setInventoryData] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [visitorData, setVisitorData] = useState(null);
  const [abandonedCarts, setAbandonedCarts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [invRes, salesRes, visRes, cartRes] = await Promise.all([
          axios.get(`${API}/reports/inventory`),
          axios.get(`${API}/reports/sales?days=30`),
          axios.get(`${API}/reports/visitors?days=7`),
          axios.get(`${API}/cart/abandoned`),
        ]);
        setInventoryData(invRes.data);
        setSalesData(salesRes.data);
        setVisitorData(visRes.data);
        setAbandonedCarts(cartRes.data);
      } catch (error) {
        console.error('Error fetching reports:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const COLORS = ['#2D4A3E', '#C9A962', '#B87A5B', '#7A8B7A'];

  if (loading) return <div className="animate-pulse"><div className="h-96 bg-gray-200 rounded-2xl" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-forest">Reports</h1>
        <p className="text-gray-500">Detailed business analytics and reports</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-sand">
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
          <TabsTrigger value="sales">Sales</TabsTrigger>
          <TabsTrigger value="visitors">Visitors</TabsTrigger>
          <TabsTrigger value="abandoned">Abandoned Carts</TabsTrigger>
        </TabsList>

        <TabsContent value="inventory" className="space-y-6">
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-2xl border">
              <p className="text-sm text-gray-500">Total Products</p>
              <p className="text-2xl font-bold text-forest">{inventoryData?.total_products || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-2xl border">
              <p className="text-sm text-gray-500">Total Stock</p>
              <p className="text-2xl font-bold text-forest">{inventoryData?.total_stock || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-2xl border">
              <p className="text-sm text-gray-500">Out of Stock</p>
              <p className="text-2xl font-bold text-red-600">{inventoryData?.out_of_stock || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-2xl border">
              <p className="text-sm text-gray-500">Inventory Value</p>
              <p className="text-2xl font-bold text-forest">${inventoryData?.inventory_value?.toFixed(2) || '0.00'}</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border">
            <h3 className="font-semibold text-forest mb-4">Full Inventory</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inventoryData?.products?.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="capitalize">{p.category?.replace(/_/g, ' ')}</TableCell>
                    <TableCell>${p.price?.toFixed(2)}</TableCell>
                    <TableCell className={p.stock === 0 ? 'text-red-600' : p.stock <= 5 ? 'text-orange-600' : ''}>
                      {p.stock}
                    </TableCell>
                    <TableCell>${(p.price * p.stock).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="sales" className="space-y-6">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white p-6 rounded-2xl border">
              <p className="text-sm text-gray-500">Total Revenue (30 days)</p>
              <p className="text-2xl font-bold text-forest">${salesData?.total_revenue?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="bg-white p-6 rounded-2xl border">
              <p className="text-sm text-gray-500">Total Orders</p>
              <p className="text-2xl font-bold text-forest">{salesData?.total_orders || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-2xl border">
              <p className="text-sm text-gray-500">Avg Order Value</p>
              <p className="text-2xl font-bold text-forest">${salesData?.avg_order_value?.toFixed(2) || '0.00'}</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border">
            <h3 className="font-semibold text-forest mb-4">Sales Trend (30 days)</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={salesData?.daily_data || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={10} />
                  <YAxis fontSize={10} />
                  <Tooltip />
                  <Line type="monotone" dataKey="revenue" stroke="#2D4A3E" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border">
            <h3 className="font-semibold text-forest mb-4">Top Selling Products</h3>
            <div className="space-y-3">
              {salesData?.top_products?.map((p, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-sand rounded-xl">
                  <span className="font-medium">{p.name}</span>
                  <div className="text-right">
                    <span className="text-sm text-gray-500">{p.quantity} sold</span>
                    <span className="ml-4 font-mono">${p.revenue?.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="visitors" className="space-y-6">
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-2xl border">
              <p className="text-sm text-gray-500">Total Sessions (7 days)</p>
              <p className="text-2xl font-bold text-forest">{visitorData?.total_sessions || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-2xl border">
              <p className="text-sm text-gray-500">Page Views</p>
              <p className="text-2xl font-bold text-forest">{visitorData?.total_page_views || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-2xl border">
              <p className="text-sm text-gray-500">Avg Session Duration</p>
              <p className="text-2xl font-bold text-forest">{visitorData?.avg_session_duration || 0}s</p>
            </div>
            <div className="bg-white p-6 rounded-2xl border">
              <p className="text-sm text-gray-500">Unique Locations</p>
              <p className="text-2xl font-bold text-forest">{visitorData?.locations?.length || 0}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-2xl border">
              <h3 className="font-semibold text-forest mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5" /> Visitor Locations
              </h3>
              <div className="space-y-2">
                {visitorData?.locations?.map((loc, i) => (
                  <div key={i} className="flex justify-between p-2 bg-sand rounded-lg">
                    <span>{loc.location}</span>
                    <span className="font-mono">{loc.visits}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl border">
              <h3 className="font-semibold text-forest mb-4">Top Pages</h3>
              <div className="space-y-2">
                {visitorData?.top_pages?.map((page, i) => (
                  <div key={i} className="flex justify-between p-2 bg-sand rounded-lg">
                    <span className="truncate">{page.page || '/'}</span>
                    <span className="font-mono">{page.views} views</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border">
            <h3 className="font-semibold text-forest mb-4">Recent Sessions</h3>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Location</TableHead>
                    <TableHead>Pages Visited</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Last Active</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visitorData?.sessions?.slice(0, 20).map((s, i) => (
                    <TableRow key={i}>
                      <TableCell>{s.location || 'Unknown'}</TableCell>
                      <TableCell>{s.pages_visited?.join(', ') || '-'}</TableCell>
                      <TableCell>{s.total_duration}s</TableCell>
                      <TableCell>{new Date(s.last_visit).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="abandoned" className="space-y-6">
          <div className="bg-white p-6 rounded-2xl border">
            <div className="flex items-center gap-2 mb-6">
              <ShoppingBag className="h-5 w-5 text-orange-500" />
              <h3 className="font-semibold text-forest">Abandoned Carts</h3>
              <span className="text-sm text-gray-500">({abandonedCarts.length} carts)</span>
            </div>

            {abandonedCarts.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No abandoned carts</p>
            ) : (
              <div className="space-y-4">
                {abandonedCarts.map((cart) => (
                  <div key={cart.id} className="p-4 bg-sand rounded-xl">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="text-sm text-gray-500">Session: {cart.session_id?.slice(0, 8)}...</p>
                        {cart.customer_email && <p className="text-sm">{cart.customer_email}</p>}
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-forest">${cart.total_amount?.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">{new Date(cart.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {cart.items?.map((item, i) => (
                        <span key={i}>{item.product_name} ({item.quantity}){i < cart.items.length - 1 ? ', ' : ''}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};


// ==================== ADMIN BLOG ====================
export const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    author: 'Reggae Essences',
    image_url: '',
    status: 'draft',
    category: 'General',
  });

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blogs`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      ...formData,
      title,
      slug: editingPost ? formData.slug : generateSlug(title),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await axios.put(`${API}/blogs/${editingPost.id}`, formData);
        toast.success('Blog post updated!');
      } else {
        await axios.post(`${API}/blogs`, formData);
        toast.success('Blog post created!');
      }
      setShowForm(false);
      setEditingPost(null);
      setFormData({
        title: '',
        slug: '',
        content: '',
        excerpt: '',
        author: 'Reggae Essences',
        image_url: '',
        status: 'draft',
        category: 'General',
      });
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    }
  };

  const handleEdit = (post) => {
    setFormData({
      title: post.title,
      slug: post.slug,
      content: post.content,
      excerpt: post.excerpt || '',
      author: post.author,
      image_url: post.image_url || '',
      status: post.status,
      category: post.category || 'General',
    });
    setEditingPost(post);
    setShowForm(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`${API}/blogs/${postId}`);
      toast.success('Post deleted');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData({ ...formData, image_url: response.data.url });
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-terracotta"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-blog" className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-forest flex items-center gap-2">
          <BookOpen className="h-6 w-6" />
          Blog Management
        </h1>
        <Button
          onClick={() => {
            setEditingPost(null);
            setFormData({
              title: '',
              slug: '',
              content: '',
              excerpt: '',
              author: 'Reggae Essences',
              image_url: '',
              status: 'draft',
              category: 'General',
            });
            setShowForm(true);
          }}
          className="bg-forest text-white"
          data-testid="new-post-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Post
        </Button>
      </div>

      {/* Blog Form Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPost ? 'Edit Post' : 'Create New Post'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <Input
                  value={formData.title}
                  onChange={handleTitleChange}
                  required
                  data-testid="post-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Slug</label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="auto-generated-from-title"
                  data-testid="post-slug-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Excerpt</label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                rows={2}
                placeholder="Brief summary of the post..."
                data-testid="post-excerpt-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Content *</label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={10}
                required
                placeholder="Write your blog post content here..."
                data-testid="post-content-input"
              />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Author</label>
                <Input
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  data-testid="post-author-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="post-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General">General</SelectItem>
                    <SelectItem value="Skincare">Skincare</SelectItem>
                    <SelectItem value="Lifestyle">Lifestyle</SelectItem>
                    <SelectItem value="Recipes">Recipes</SelectItem>
                    <SelectItem value="News">News</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="post-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Featured Image</label>
              <div className="flex gap-4 items-start">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="flex-1"
                />
                {formData.image_url && (
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    className="w-20 h-20 object-cover rounded"
                  />
                )}
              </div>
              {formData.image_url && (
                <p className="text-xs text-gray-500 mt-1 truncate">{formData.image_url}</p>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-forest text-white" data-testid="save-post-btn">
                {editingPost ? 'Update Post' : 'Create Post'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Posts Table */}
      <div className="bg-white rounded-2xl border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Author</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {posts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                  No blog posts yet. Create your first post!
                </TableCell>
              </TableRow>
            ) : (
              posts.map((post) => (
                <TableRow key={post.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {post.image_url && (
                        <img
                          src={post.image_url}
                          alt=""
                          className="w-10 h-10 object-cover rounded"
                        />
                      )}
                      <div>
                        <p className="font-medium">{post.title}</p>
                        <p className="text-xs text-gray-500">/{post.slug}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{post.category || 'General'}</TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        post.status === 'published'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {post.status}
                    </span>
                  </TableCell>
                  <TableCell>{post.author}</TableCell>
                  <TableCell>
                    {new Date(post.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                        data-testid={`view-post-${post.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(post)}
                        data-testid={`edit-post-${post.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(post.id)}
                        data-testid={`delete-post-${post.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};


// ==================== ADMIN REVIEWS ====================
export const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProduct, setFilterProduct] = useState('all');

  const fetchData = async () => {
    try {
      const [reviewsRes, productsRes] = await Promise.all([
        axios.get(`${API}/reviews`),
        axios.get(`${API}/products?include_inactive=true`)
      ]);
      setReviews(reviewsRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      await axios.delete(`${API}/reviews/${reviewId}`);
      toast.success('Review deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product?.name || 'Unknown Product';
  };

  const filteredReviews = filterProduct === 'all' 
    ? reviews 
    : reviews.filter(r => r.product_id === filterProduct);

  return (
    <div data-testid="admin-reviews" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-forest">Reviews</h1>
          <p className="text-gray-500">Manage customer reviews</p>
        </div>
        <Select value={filterProduct} onValueChange={setFilterProduct}>
          <SelectTrigger className="w-60">
            <SelectValue placeholder="Filter by product" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Products</SelectItem>
            {products.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-gray-200 rounded-xl" />)}</div>
      ) : filteredReviews.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl text-center">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No reviews found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredReviews.map((review) => (
            <div key={review.id} className="bg-white p-6 rounded-2xl border border-gray-100">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-2">
                    <h3 className="font-semibold text-forest">{review.customer_name}</h3>
                    <div className="flex">
                      {[1,2,3,4,5].map(star => (
                        <span key={star} className={`text-lg ${star <= review.rating ? 'text-gold' : 'text-gray-300'}`}>★</span>
                      ))}
                    </div>
                    <span className="text-xs text-gray-400">
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-terracotta mb-2">Product: {getProductName(review.product_id)}</p>
                  <p className="text-gray-600">{review.comment}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => handleDelete(review.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== ADMIN POLICIES ====================
export const AdminPolicies = () => {
  const [policies, setPolicies] = useState({
    shipping: '',
    returns: '',
    refunds: '',
    cancellation: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchPolicies = async () => {
    try {
      const response = await axios.get(`${API}/policies`);
      setPolicies(response.data);
    } catch (error) {
      console.error('Error fetching policies:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPolicies(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/policies`, policies);
      toast.success('Policies updated successfully!');
    } catch (error) {
      toast.error('Failed to save policies');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">{[1,2,3,4].map(i => <div key={i} className="h-40 bg-gray-200 rounded-xl" />)}</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-policies" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-forest">Store Policies</h1>
          <p className="text-gray-500">Manage shipping, returns, refunds and cancellation policies</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-forest text-white rounded-full">
          {saving ? 'Saving...' : 'Save All Policies'}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Shipping Policy */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100">
          <h2 className="font-semibold text-forest text-xl mb-4 flex items-center gap-2">
            <Package className="h-5 w-5" /> Shipping Policy
          </h2>
          <Textarea
            value={policies.shipping}
            onChange={(e) => setPolicies({ ...policies, shipping: e.target.value })}
            rows={8}
            placeholder="Enter your shipping policy details..."
            className="rounded-xl"
          />
        </div>

        {/* Returns Policy */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100">
          <h2 className="font-semibold text-forest text-xl mb-4 flex items-center gap-2">
            <Package className="h-5 w-5" /> Returns Policy
          </h2>
          <Textarea
            value={policies.returns}
            onChange={(e) => setPolicies({ ...policies, returns: e.target.value })}
            rows={8}
            placeholder="Enter your returns policy details..."
            className="rounded-xl"
          />
        </div>

        {/* Refunds Policy */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100">
          <h2 className="font-semibold text-forest text-xl mb-4 flex items-center gap-2">
            <DollarSign className="h-5 w-5" /> Refunds Policy
          </h2>
          <Textarea
            value={policies.refunds}
            onChange={(e) => setPolicies({ ...policies, refunds: e.target.value })}
            rows={8}
            placeholder="Enter your refunds policy details..."
            className="rounded-xl"
          />
        </div>

        {/* Cancellation Policy */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100">
          <h2 className="font-semibold text-forest text-xl mb-4 flex items-center gap-2">
            <XCircle className="h-5 w-5" /> Cancellation Policy
          </h2>
          <Textarea
            value={policies.cancellation}
            onChange={(e) => setPolicies({ ...policies, cancellation: e.target.value })}
            rows={8}
            placeholder="Enter your cancellation policy details..."
            className="rounded-xl"
          />
        </div>
      </div>
    </div>
  );
};
